import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { Heart, Shield, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

function DonorboxWidget() {
  const containerRef = useRef(null);

  useEffect(() => {
    // Load the Donorbox widget script
    const existingScript = document.querySelector('script[src="https://donorbox.org/widgets.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://donorbox.org/widgets.js';
      script.type = 'module';
      script.async = true;
      document.body.appendChild(script);
    }

    // Create the widget element
    if (containerRef.current && !containerRef.current.querySelector('dbox-widget')) {
      const widget = document.createElement('dbox-widget');
      widget.setAttribute('campaign', 'goodliving-scholars-scholarship-937803');
      widget.setAttribute('type', 'donation_form');
      widget.setAttribute('enable-auto-scroll', 'true');
      containerRef.current.appendChild(widget);
    }
  }, []);

  return <div ref={containerRef} data-testid="donorbox-widget" />;
}

export default function Donate() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-cream" data-testid="donate-page">
      {/* Header */}
      <section className="bg-cabernet py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <p className="text-sm uppercase tracking-[0.2em] font-accent font-bold text-brass mb-3">
            Support Our Mission
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-cream tracking-tight leading-none mb-4">
            Make an Impact Today
          </h1>
          <p className="font-body text-base sm:text-lg text-cream/80 max-w-2xl leading-relaxed">
            Every dollar you give goes directly toward scholarships and mentorship 
            programs for aspiring young entrepreneurs.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Why Donate */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-heading font-medium text-cabernet mb-4">
                  Why Your Donation Matters
                </h2>
                <p className="font-body text-base text-ink/80 leading-relaxed">
                  As a 501(c)(3) non-profit, 100% of your contribution is tax-deductible and 
                  goes directly to supporting students. Your generosity funds scholarships, 
                  international project placements, and mentorship programs.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: Heart,
                    title: 'Direct Student Impact',
                    desc: 'Every donation funds real scholarship awards and program costs.',
                  },
                  {
                    icon: Shield,
                    title: 'Tax Deductible',
                    desc: 'All contributions are fully tax-deductible under 501(c)(3).',
                  },
                  {
                    icon: TrendingUp,
                    title: 'Transparent Reporting',
                    desc: 'Track your impact through our donor portal with real-time metrics.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 p-4 bg-card-surface rounded-lg border border-brass/20">
                    <item.icon className="w-6 h-6 text-brass shrink-0 mt-0.5" />
                    <div>
                      <p className="font-accent text-sm font-semibold text-ink">{item.title}</p>
                      <p className="font-body text-sm text-ink/60">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {user ? (
                <Link to="/dashboard">
                  <Button
                    className="bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm px-6 py-5"
                    data-testid="go-to-donor-portal-btn"
                  >
                    Go to Donor Portal
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button
                    className="bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm px-6 py-5"
                    data-testid="sign-in-to-donate-btn"
                  >
                    Sign In to Access Donor Portal
                  </Button>
                </Link>
              )}
            </div>

            {/* Donorbox Embed */}
            <div className="rounded-lg overflow-hidden border border-brass/30 shadow-md bg-card-surface p-4" data-testid="donate-page-donorbox">
              <DonorboxWidget />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
